import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import Section from '../components/Section';
import { fetchTeamMembers, fetchTeamPage } from '../lib/sanity';
import type { TeamMember, TeamPageContent } from '../lib/types';

const Team: React.FC = () => {
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [pageContent, setPageContent] = useState<TeamPageContent | null>(null);

  useEffect(() => {
    fetchTeamMembers().then((data) => {
      if (data && data.length > 0) {
        setTeamMembers(data);
      }
    });
    fetchTeamPage().then((data) => {
      if (data) setPageContent(data);
    });
  }, []);

  return (
    <main className="pt-20">
      <Section padding="default">
        {pageContent && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center mb-16"
          >
            <h1 className="text-5xl md:text-6xl font-playfair font-bold text-primary-black mb-6">
              {pageContent.headerTitle}{' '}
              <span className="text-primary-red">{pageContent.headerHighlight}</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-700 leading-relaxed">
              {pageContent.headerDescription}
            </p>
          </motion.div>
        )}

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
          {teamMembers.map((member, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow duration-200"
            >
              {/* Full-width portrait photo */}
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src={member.image}
                  alt={member.name}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              {/* Name + role below */}
              <div className="px-6 py-5">
                <h3 className="text-xl font-playfair font-bold text-primary-black mb-1">
                  {member.name}
                </h3>
                <div className="text-primary-red font-inter font-medium text-sm">
                  {member.role}
                </div>
                {member.bio ? (
                  <p className="text-gray-600 text-sm leading-relaxed mt-3">{member.bio}</p>
                ) : null}
              </div>
            </motion.div>
          ))}
        </div>

        {pageContent && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-gradient-to-br from-secondary-sage/20 to-secondary-terracotta/20 rounded-2xl p-8 md:p-12"
          >
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-playfair font-bold text-primary-black mb-6">
                {pageContent.ctaTitle}
              </h2>
              <p className="text-lg md:text-xl text-gray-700 mb-8">
                {pageContent.ctaDescription}
              </p>
              <a
                href={`mailto:${pageContent.ctaEmail}`}
                className="inline-block px-8 py-4 bg-primary-red hover:bg-red-700 text-white font-inter font-semibent rounded-lg shadow-md hover:shadow-lg transition-all duration-200"
              >
                {pageContent.ctaButtonText}
              </a>
            </div>
          </motion.div>
        )}
      </Section>
    </main>
  );
};

export default Team;
